(() => {
  const root = document.querySelector('[data-lt-sample-selector]');
  if (!root) return;

  const cards = [...root.querySelectorAll('[data-sample-card]')];
  const variantIds = new Set(cards.map((card) => Number(card.dataset.variantId)));
  const maxSamples = Number(root.dataset.maxSamples || 3);
  const maxPerVariant = Number(root.dataset.maxPerVariant || 1);
  const selectedCounts = [...root.querySelectorAll('[data-selected-count]')];
  const progress = root.querySelector('[data-progress]');
  const status = root.querySelector('[data-selector-status]');
  const speciesTabs = [...root.querySelectorAll('[data-species-tab]')];
  const speciesPanels = [...root.querySelectorAll('[data-species-panel]')];
  const TOTAL_LIMIT_MESSAGE = 'Izberete lahko največ 3 brezplačne vzorce.';
  const VARIANT_LIMIT_MESSAGE = 'Vsako recepturo lahko izberete največ enkrat.';

  const state = { cart: null, total: 0, busy: false, messageTimer: null };

  const endpoint = (route, fallback) => `${route || fallback}.js`;
  const cartUrl = () => endpoint(window.routes?.cart_url, '/cart');
  const addUrl = () => endpoint(window.routes?.cart_add_url, '/cart/add');
  const changeUrl = () => endpoint(window.routes?.cart_change_url, '/cart/change');

  const showMessage = (message) => {
    if (!status) return;
    status.textContent = message;
    status.hidden = false;
    clearTimeout(state.messageTimer);
    state.messageTimer = setTimeout(() => { status.hidden = true; }, 3800);
  };

  const parseResponse = async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'Spremembe ni bilo mogoče shraniti.');
    return data;
  };

  const sampleLines = () => (state.cart?.items || []).filter((item) => variantIds.has(Number(item.variant_id)));
  const getLine = (variantId) => sampleLines().find((item) => Number(item.variant_id) === Number(variantId));

  const updateCartBubble = (sections) => {
    const html = sections?.['cart-icon-bubble'];
    const target = document.getElementById('cart-icon-bubble');
    if (!html || !target) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const source = doc.querySelector('.shopify-section') || doc.body;
    target.innerHTML = source.innerHTML;
  };

  const activateSpecies = (species, focus = false) => {
    speciesTabs.forEach((tab) => {
      const active = tab.dataset.speciesTab === species;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      tab.classList.toggle('is-active', active);
      if (active && focus) tab.focus();
    });

    speciesPanels.forEach((panel) => {
      panel.hidden = panel.dataset.speciesPanel !== species;
    });
  };

  speciesTabs.forEach((tab, index) => {
    tab.addEventListener('click', () => activateSpecies(tab.dataset.speciesTab));
    tab.addEventListener('keydown', (event) => {
      let nextIndex = null;
      if (event.key === 'ArrowRight') nextIndex = (index + 1) % speciesTabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (index - 1 + speciesTabs.length) % speciesTabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = speciesTabs.length - 1;
      if (nextIndex === null) return;
      event.preventDefault();
      activateSpecies(speciesTabs[nextIndex].dataset.speciesTab, true);
    });
  });

  const render = () => {
    state.total = sampleLines().reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    selectedCounts.forEach((node) => { node.textContent = String(state.total); });
    if (progress) progress.style.width = `${Math.min(100, (state.total / maxSamples) * 100)}%`;

    cards.forEach((card) => {
      const variantId = Number(card.dataset.variantId);
      const quantity = Number(getLine(variantId)?.quantity || 0);
      const choose = card.querySelector('[data-action="increase"]');
      const remove = card.querySelector('[data-action="decrease"]');
      card.classList.toggle('is-selected', quantity > 0);
      card.classList.toggle('is-busy', state.busy);
      if (choose) {
        choose.disabled = state.busy;
        choose.setAttribute('aria-disabled', String(choose.disabled));
      }
      if (remove) {
        remove.disabled = state.busy;
        remove.setAttribute('aria-disabled', String(remove.disabled));
      }
    });
  };

  const fetchCart = async () => {
    const response = await fetch(cartUrl(), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    state.cart = await parseResponse(response);
    render();
    return state.cart;
  };

  const increase = async (card) => {
    if (!state.cart) await fetchCart();
    const variantId = Number(card.dataset.variantId);
    const currentQuantity = Number(getLine(variantId)?.quantity || 0);

    if (currentQuantity >= maxPerVariant) {
      showMessage(VARIANT_LIMIT_MESSAGE);
      return;
    }
    if (state.total >= maxSamples) {
      showMessage(TOTAL_LIMIT_MESSAGE);
      return;
    }

    state.busy = true;
    render();
    try {
      const response = await fetch(addUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: ['cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });
      const addResponse = await parseResponse(response);
      updateCartBubble(addResponse.sections);
      await fetchCart();
      await window.ltSampleLimit?.refresh?.();
    } catch (error) {
      console.error('Lačne Tačke – izbor vzorca:', error);
      showMessage(error.message || 'Vzorec trenutno ni na voljo.');
      await fetchCart().catch(() => null);
    } finally {
      state.busy = false;
      render();
    }
  };

  const decrease = async (card) => {
    if (!state.cart) await fetchCart();
    const variantId = Number(card.dataset.variantId);
    const line = getLine(variantId);
    if (!line) return;

    state.busy = true;
    render();
    try {
      const response = await fetch(changeUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: line.key,
          quantity: 0,
          sections: ['cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });
      const cartResponse = await parseResponse(response);
      state.cart = cartResponse;
      updateCartBubble(cartResponse.sections);
      render();
      await window.ltSampleLimit?.refresh?.();
    } catch (error) {
      console.error('Lačne Tačke – odstranitev vzorca:', error);
      showMessage(error.message || 'Vzorca trenutno ni mogoče odstraniti.');
      await fetchCart().catch(() => null);
    } finally {
      state.busy = false;
      render();
    }
  };

  root.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-action]');
    if (!button || button.disabled || state.busy) return;
    const card = button.closest('[data-sample-card]');
    if (!card) return;
    if (button.dataset.action === 'increase') await increase(card);
    if (button.dataset.action === 'decrease') await decrease(card);
  });

  document.addEventListener('lt:samples:updated', (event) => {
    if (!event.detail?.ready || state.busy) return;
    if (Number(event.detail.count) !== state.total) fetchCart().catch(() => null);
  });

  window.addEventListener('pageshow', () => {
    fetchCart().catch(() => null);
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target?.querySelector?.('[data-lt-sample-selector]')) fetchCart().catch(() => null);
  });

  activateSpecies(speciesTabs.find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.speciesTab || 'dog');

  fetchCart().catch((error) => {
    console.warn('Lačne Tačke – stanje vzorcev:', error);
    showMessage('Stanja košarice trenutno ni mogoče prebrati. Osvežite stran.');
  });
})();
